<template>
  <div class="chart-wrapper" :style="{ height: `${height}px` }">
    <canvas ref="chartRef"></canvas>
  </div>
</template>

<script setup lang="ts">
import Chart from "@/chart-setup";
import { PALETTE } from "@/composables/chartPalette";
import { type ChartConfiguration } from "chart.js";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  useTemplateRef,
  watch,
} from "vue";

const props = withDefaults(
  defineProps<{
    autoData: number[];
    manualData: number[];
    labels: string[];
    autoLabel?: string;
    manualLabel?: string;
    title?: string;
    subtitle?: string;
    yAxisTitle: string;
    height?: number;
  }>(),
  {
    autoLabel: "Automatic",
    manualLabel: "Manual",
    title: "",
    subtitle: "",
    height: 250,
  }
);

const chartRef = useTemplateRef<HTMLCanvasElement>("chartRef");
let chart: Chart<any> | null = null;

const autoMarker = (() => {
  const canvas = document.createElement("canvas");
  canvas.width = 32;
  canvas.height = 6;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.fillStyle = PALETTE.semanticOverrides.status["Non-transferable"];
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  return canvas;
})();

// Chart.js paints the lowest `order` last, breaking ties by dataset index, so without explicit
// orders the manual bars paint over every automatic marker that sits below the bar's top.
const internalChartData = computed(() => ({
  labels: props.labels,
  datasets: [
    {
      type: "bar" as const,
      label: props.manualLabel,
      data: props.manualData,
      order: 1,
      backgroundColor: PALETTE.semanticOverrides.status.Available,
      borderColor: PALETTE.semanticOverrides.status.Available,
      borderWidth: 1,
    },
    {
      type: "line" as const,
      label: props.autoLabel,
      data: props.autoData,
      order: 0,
      showLine: false,
      pointStyle: autoMarker,
      backgroundColor: PALETTE.semanticOverrides.status["Non-transferable"],
    },
  ],
}));

const createChart = () => {
  if (!chartRef.value) return;
  const ctx = chartRef.value.getContext("2d");
  if (!ctx) return;

  const config: ChartConfiguration = {
    type: "bar",
    data: internalChartData.value as any,
    options: {
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        legend: {
          display: true,
          position: "top",
        },
        title: {
          display: !!props.title,
          text: props.title,
          font: { size: 16, family: "Nunito Sans" },
          padding: { top: 5, bottom: 0 },
        },
        subtitle: {
          display: !!props.subtitle,
          text: props.subtitle,
          font: { size: 14, style: "italic", family: "Nunito Sans" },
          padding: { top: 0, bottom: 0 },
        },
        tooltip: {
          callbacks: {
            label: function (context: any) {
              let label = context.dataset.label || "";
              if (label) label += ": ";
              if (context.parsed.y !== null)
                label += Math.round(context.parsed.y) + " " + props.yAxisTitle;
              return label;
            },
          },
        },
      },
      scales: {
        x: {},
        y: {
          beginAtZero: true,
          title: { display: true, text: props.yAxisTitle },
        },
      },
      responsive: true,
      maintainAspectRatio: false,
    },
  };

  chart = new Chart(ctx, config);
};

const updateChart = () => {
  if (!chart) return;
  chart.data = internalChartData.value as any;
  const yScale = chart.options?.scales?.y;
  if (yScale && !Array.isArray(yScale)) {
    yScale.title = {
      ...(yScale.title ?? {}),
      display: true,
      text: props.yAxisTitle,
    };
  }
  if (chart.options?.plugins?.title) {
    chart.options.plugins.title.text = props.title;
    chart.options.plugins.title.display = !!props.title;
  }
  if (chart.options?.plugins?.subtitle) {
    chart.options.plugins.subtitle.text = props.subtitle;
    chart.options.plugins.subtitle.display = !!props.subtitle;
  }
  chart.update();
};

onMounted(async () => {
  await nextTick();
  createChart();
});

onBeforeUnmount(() => {
  if (chart) {
    chart.destroy();
    chart = null;
  }
});

watch(() => internalChartData.value, updateChart, { deep: true });
watch(() => props.title, updateChart);
watch(() => props.subtitle, updateChart);
watch(() => props.yAxisTitle, updateChart);
watch(
  () => props.height,
  async () => {
    await nextTick();
    chart?.resize();
  }
);
</script>

<style scoped lang="scss">
.chart-wrapper {
  position: relative;
  margin-bottom: 15px;
  height: var(--chart-height, 250px);
}
</style>
